import { useRef, useState, forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { TEMPLATE_CONFIGS, type TemplateType } from './templateConfigs';

interface CertificatePreviewProps {
  // 템플릿 타입
  templateType?: TemplateType;

  // 테두리
  borderColor: string;
  borderThickness: number;

  // 로고
  logoUrl: string | null;

  // 서명
  signatureUrl: string | null;
  // 두 번째 강사 서명 (template_type 3 전용)
  signatureUrl2?: string | null;

  // 텍스트
  courseName: string;
  signerName: string;
  signerTitle: string;
  // 두 번째 강사 이름 (template_type 3 전용)
  signerName2?: string | null;

  // 더미 데이터
  studentName?: string;
  certificateNumber?: string;
  completionDate?: string;

  // 총 수업 시간
  totalHours?: number;

  // QR 코드
  qrCodeUrl?: string;

  // 캡처 모드 (썸네일 생성 시 인터랙티브 요소 숨김)
  captureMode?: boolean;
}

const CertificatePreview = forwardRef<HTMLDivElement, CertificatePreviewProps>(({
  templateType = 1,
  borderColor,
  borderThickness,
  logoUrl,
  signatureUrl,
  signatureUrl2,
  courseName,
  signerName,
  signerTitle,
  signerName2,
  totalHours,
  studentName = 'Sungjea Kim',
  certificateNumber = 'SF-2026-001234',
  completionDate = '2026.01.13',
  qrCodeUrl,
  captureMode = false,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // 템플릿 설정 가져오기
  const config = TEMPLATE_CONFIGS[templateType];
  const templateImageSrc = config.backgroundImage;

  // 도장 렌더 높이 (config 우선, 없으면 기본값)
  const sealHeight = config.qrCode.sealHeight ?? 'clamp(60px, 7vw, 100px)';
  // QR 박스 크기 (config 우선). 도장 PNG 투명여백(~40%)을 감안해 실제 도장 높이와 맞춤
  const qrBoxSize = config.qrCode.qrSize ?? 'clamp(40px, 4vw, 60px)';

  // 캡처(html2canvas)용 background-image 스타일.
  // html2canvas 1.4.1은 <img>의 object-fit(contain)을 지원하지 않아 박스 크기로 늘려버린다.
  // 캡처 모드에서는 background-size:contain 으로 렌더해 비율을 유지한다.
  const captureBgStyle = (
    src: string,
    position: React.CSSProperties,
    size: { width?: string; height?: string; maxHeight?: string } | undefined,
  ): React.CSSProperties => ({
    ...position,
    width: size?.width,
    // height:'auto'는 div에서 0이 되므로 maxHeight를 실제 높이로 사용
    height: size?.height === 'auto' ? size?.maxHeight ?? size?.height : size?.height,
    backgroundImage: `url(${src})`,
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center bottom',
  });

  // 템플릿 날짜 표기: config.dateMonthName이면 "May 12, 2026" 형태로, datePrefix가 있으면 앞에 접두사.
  // 입력은 "YYYY.MM.DD" / "YYYY-MM-DD"(편집기 더미·발급 포맷). 파싱 실패 시 원문 유지.
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const formatConfigDate = (s: string): string => {
    let body = s;
    if (config.dateMonthName) {
      const m = s.match(/(\d{4})[.\-/]\s*(\d{1,2})[.\-/]\s*(\d{1,2})/);
      if (m) body = `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
    }
    return (config.datePrefix ?? '') + body;
  };

  return (
    <div className="cert-outer relative w-full bg-gray-100 rounded-lg p-2 flex items-center justify-center">
      {/* A4 가로 비율 컨테이너 (297x210mm = 1.414:1) */}
      <div
        ref={ref || containerRef}
        className="cert-card relative bg-white shadow-2xl overflow-hidden"
        style={{
          width: '100%',
          // 세로형은 폭을 좁게(707px→높이 1000px), 가로형은 1000px
          maxWidth: config.portrait ? '707px' : '1000px',
          aspectRatio: config.portrait ? '1 / 1.414' : '1.414 / 1', // A4 세로/가로 비율
        }}
      >
        {/* 배경 이미지 */}
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">템플릿 로딩 중...</p>
            </div>
          </div>
        )}
        {imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <p className="text-sm text-red-600 mb-2">템플릿 이미지를 불러올 수 없습니다</p>
              <p className="text-xs text-gray-500">{templateImageSrc}</p>
            </div>
          </div>
        )}
        <img
          src={templateImageSrc}
          alt="Certificate Background"
          className="absolute inset-0 w-full h-full object-fill pointer-events-none"
          style={{ display: 'block' }}
          onLoad={() => {
            console.log('Template image loaded successfully');
            setImageLoaded(true);
          }}
          onError={(e) => {
            console.error('Failed to load template image:', e);
            setImageError(true);
          }}
        />

        {/* 테두리 */}
        {config.hasBorder && (
          <div
            className="absolute pointer-events-none"
            style={{
              top: '2%',
              left: '2%',
              right: '2%',
              bottom: '2%',
              border: `${borderThickness}px solid ${borderColor}`,
              borderRadius: '4px',
            }}
          />
        )}

        {/* 로고 - 고정 위치 (logoOpacity가 있으면 중앙 배경 워터마크) */}
        {logoUrl &&
          (captureMode ? (
            <div
              className="absolute pointer-events-none"
              style={{ ...captureBgStyle(logoUrl, config.logo.position, config.logo.size), opacity: config.logoOpacity ?? 1 }}
            />
          ) : (
            <img
              src={logoUrl}
              alt="Logo"
              className="absolute pointer-events-none object-contain"
              style={{
                ...config.logo.position,
                ...config.logo.size,
                opacity: config.logoOpacity ?? 1,
              }}
            />
          ))}

        {/* 수강생 이름 - 중앙 (Certificate of Completion 아래) */}
        <div
          className="absolute pointer-events-none text-center"
          style={{
            ...config.studentName.position,
            width: config.studentName.size?.width ?? '80%',
          }}
        >
          <div style={config.studentName.text}>
            {studentName}
          </div>
        </div>

        {/* 코스명 - 중앙 하단 ("Has successfully completed" 아래) */}
        <div
          className="absolute pointer-events-none text-center"
          style={{
            ...config.courseName.position,
            width: config.courseName.size?.width ?? '70%',
          }}
        >
          <div style={config.courseName.text}>
            {courseName || '(코스명을 입력하세요)'}{!config.hideCourseHours && totalHours ? ` (${totalHours}h)` : ''}
          </div>
        </div>

        {/* 서명 (강사 1) - 고정 위치 */}
        {signatureUrl &&
          (captureMode ? (
            <div
              className="absolute pointer-events-none"
              style={captureBgStyle(signatureUrl, config.signature.position, config.signature.size)}
            />
          ) : (
            <img
              src={signatureUrl}
              alt="Signature"
              className="absolute pointer-events-none object-contain"
              style={{
                ...config.signature.position,
                ...config.signature.size,
              }}
            />
          ))}

        {/* 두 번째 서명 (강사 2) - template_type 3 전용 */}
        {config.dualInstructor && config.signature2 && signatureUrl2 &&
          (captureMode ? (
            <div
              className="absolute pointer-events-none"
              style={captureBgStyle(signatureUrl2, config.signature2.position, config.signature2.size)}
            />
          ) : (
            <img
              src={signatureUrl2}
              alt="Signature 2"
              className="absolute pointer-events-none object-contain"
              style={{
                ...config.signature2.position,
                ...config.signature2.size,
              }}
            />
          ))}

        {/* 담당자/강사 1 정보 */}
        <div
          className="absolute pointer-events-none text-center"
          style={config.signerInfo.container.position}
        >
          <div style={config.signerInfo.name}>
            {signerName || (config.dualInstructor || config.hideSignerTitle ? '(강사 1 이름 또는 직책)' : '(담당자 이름)')}
          </div>
          {/* 직위 — 강사 2명(dualInstructor)·강사 1명 직위없음(hideSignerTitle) 템플릿에서는 표시 안 함 */}
          {!config.dualInstructor && !config.hideSignerTitle && (
            <div style={config.signerInfo.title}>
              {signerTitle || '(담당자 직위)'}
            </div>
          )}
        </div>

        {/* 강사 2 정보 - template_type 3 전용 */}
        {config.dualInstructor && config.signerInfo2 && (
          <div
            className="absolute pointer-events-none text-center"
            style={config.signerInfo2.container.position}
          >
            <div style={config.signerInfo2.name}>
              {signerName2 || '(강사 2 이름)'}
            </div>
          </div>
        )}

        {/* QR코드 + 도장 + 인증서 번호 */}
        <div
          className="absolute pointer-events-none"
          style={config.qrCode.container.position}
        >
          <div
            className={
              config.qrCode.layout === 'vertical'
                ? 'flex flex-col items-center gap-1.5 mb-1.5'
                : 'flex items-center gap-1 mb-1.5'
            }
          >
            {/* QR 코드 */}
            {qrCodeUrl ? (
              <div
                className="bg-white border border-gray-300 rounded flex items-center justify-center p-1"
                style={{
                  width: qrBoxSize,
                  height: qrBoxSize,
                }}
              >
                <QRCodeSVG
                  value={qrCodeUrl}
                  size={256}
                  level="M"
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                />
              </div>
            ) : (
              <div
                className="bg-gray-200 border border-gray-400 rounded flex items-center justify-center"
                style={{
                  width: qrBoxSize,
                  height: qrBoxSize,
                }}
              >
                <span
                  style={{
                    fontSize: 'clamp(7px, 0.75vw, 10px)',
                    color: '#6b7280',
                    fontWeight: 'bold',
                  }}
                >
                  QR
                </span>
              </div>
            )}
            {/* StampFit 공식 도장 (기본 template 2와 동일한 큰 사이즈, config.qrCode.sealHeight로 개별 조절) */}
            <img
              src="/logo/stampfit-stamp.png"
              alt="StampFit Official Seal"
              className="object-contain"
              style={{
                height: sealHeight,
                width: 'auto',
              }}
            />
          </div>
          {/* 고유번호 — 도장 PNG 하단 투명여백(~20%)만큼 끌어올려 도장 실제 바닥에 밀착 (가로 배치 한정).
              도장이 작아 QR과 높이가 비슷하면 serialPullRatio를 낮춰 QR 영역 침범 방지 */}
          <div
            style={{
              ...config.qrCode.text,
              marginTop:
                config.qrCode.layout === 'horizontal'
                  ? `calc(${sealHeight} * ${-(config.qrCode.serialPullRatio ?? 0.22)})`
                  : undefined,
            }}
          >
            {certificateNumber}
          </div>
        </div>

        {/* 완료 날짜 (한 줄 유지 — 중앙 정렬 배치 시 좁은 가용폭에서 줄바꿈 방지) */}
        <div
          className="absolute pointer-events-none"
          style={{ ...config.completionDate.position, whiteSpace: 'nowrap' }}
        >
          <div style={config.completionDate.text}>
            {formatConfigDate(completionDate)}
          </div>
        </div>

        {/* "DATE" 고정 라벨 (배경에 baked 안 된 템플릿용, 예: template 10) */}
        {config.dateLabel && (
          <div
            className="absolute pointer-events-none text-center"
            style={config.dateLabel.container.position}
          >
            <div style={config.dateLabel.text}>DATE</div>
          </div>
        )}
      </div>

    </div>
  );
});

CertificatePreview.displayName = 'CertificatePreview';

export default CertificatePreview;
